import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
    Aangever,
    AangeverBurgerlijkeStaat,
    AangeverSamenwoonSituatie,
    Aangifte,
    BokkzAlgDataService,
    BokkzIbDataService,
    IncomeTaxEntity, Periode, Woonland
} from '@snelstart-ventures/fiscaal/domain-logic';
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { SharedUiTooltipModule } from '@snelstart/ui-tooltip';
import { CommonModule } from '@angular/common';
import { SharedUiAlertModule } from '@snelstart/ui-alert';
import {
    optionsLandcodesMetNLD,
    optionsLandcodesZonderNLD
} from '../../gedeelde-componenten/gedeelde-componenten.component';
import { SharedUiSearchControlModule } from '@snelstart/ui-search-control';

@Component({
    selector: 'snelstart-venture-fiscaal-feature-aangever',
    templateUrl: './aangever.component.html',
    styleUrls: ['./aangever.component.scss'],
    imports: [SharedUiDetailsModule, SharedUiFormControlsModule, SharedUiTooltipModule, CommonModule, SharedUiAlertModule, SharedUiSearchControlModule]
})
export class AangeverComponent implements OnInit, OnDestroy {

    @Input() public id!: string;
    protected readonly AangeverSamenwoonSituatie = AangeverSamenwoonSituatie;
    protected readonly AangeverBurgerlijkeStaat = AangeverBurgerlijkeStaat;
    private destroy$ = new Subject<void>();
    public clientId!: string;
    public incomeTaxEntity = {} as IncomeTaxEntity;
    public aangeverFg = {} as FormGroup;
    public gezamenlijkeAangifteFg = {} as FormGroup;
    public woonlandFg = {} as FormGroup;
    private isKwalificerendBtlSubscribtion: Subscription | undefined;
    public isKwalificerendBtl: boolean | undefined = false;

    public optionsJaNee: SelectFormControlItem<boolean | undefined>[] = [
        { value: true, label: 'Ja' },
        { value: false, label: 'Nee' }
    ];

    public optionsLandcodesMetNld: SelectFormControlItem<string>[] = optionsLandcodesMetNLD;

    public optionsLandcodesZonderNld: SelectFormControlItem<string>[] = optionsLandcodesZonderNLD;

    public optiesBurgerlijkeStaat: SelectFormControlItem<AangeverBurgerlijkeStaat | null>[] = [
        {
            value: AangeverBurgerlijkeStaat.ALLEENSTAAND,
            label: 'Hele jaar alleenstaand'
        },
        {
            value: AangeverBurgerlijkeStaat.GEHUWD,
            label: 'Hele jaar gehuwd'
        },
        {
            value: AangeverBurgerlijkeStaat.SAMENWONEND,
            label: 'Hele jaar samenwonend'
        },
        {
            value: AangeverBurgerlijkeStaat.SITUATIE_GEWIJZIGD,
            label: `Gewijzigd/deels samenwonend`
        }
    ];

    public optiesSamenwonend: SelectFormControlItem<AangeverSamenwoonSituatie | null>[] = [
        {
            value: AangeverSamenwoonSituatie.NOTARIEEL_SAMENLEVINGS_CONTRACT,
            label: 'Samen een notarieel samenlevingscontract'
        },
        {
            value: AangeverSamenwoonSituatie.SAMEN_KIND,
            label: 'Samen een kind'
        },
        {
            value: AangeverSamenwoonSituatie.KIND_ANDER_ERKEND,
            label: 'De ene heeft kind van ander erkend'
        },
        {
            value: AangeverSamenwoonSituatie.SAMEN_EIGENAAR_WONING,
            label: 'Samen eigenaar woning (hoofdverblijf)'
        },
        {
            value: AangeverSamenwoonSituatie.MINDERJARIG_KIND_OP_WOONADRES,
            label: 'Minderjarig kind op woonadres'
        },
        {
            value: AangeverSamenwoonSituatie.VORIG_JAAR_FISCAAL_PARTNER,
            label: 'Samen vorig (aangifte)jaar fiscaal partners'
        },
        {
            value: AangeverSamenwoonSituatie.PARTNER_PENSIOENREGELING,
            label: 'Partners in een pensioenregeling'
        },
        {
            value: AangeverSamenwoonSituatie.GEEN,
            label: 'Geen van voorgaande situaties'
        }
    ];

    constructor(
        public dataService: BokkzIbDataService,
        public dataAlgService: BokkzAlgDataService,
        private cdr: ChangeDetectorRef,
        private fb: FormBuilder
    ) {
        this.aangeverFg = this.fb.group<Aangever>({
            voorletters: null as unknown as string,
            roepnaam: null as unknown as string,
            tussenvoegsels: null as unknown as string,
            achternaam: null as unknown as string,
            geboortedatum: null as unknown as Date,
            overlijdensdatum: null as unknown as Date,
            bsn: null as unknown as string,
            burgerlijkeStaat: null as unknown as AangeverBurgerlijkeStaat,
            samenwoonSituatie: null as unknown as AangeverSamenwoonSituatie | undefined,
            landCodeNationaliteit: null as unknown as string
        });
        this.gezamenlijkeAangifteFg = this.fb.group({
            gezamenlijkeAangifte: null as unknown as boolean
        });
        this.woonlandFg = this.fb.group({
            inwonerLandNonDiscriminatie: false,
            woonlandVoldoetVoorKwalificatie: false,
            belastingplichtigeVoldoetAanVoorwaardenKwalificatie: false,
            landCodeWoonland: null as unknown as string,
            periodeWoonachtigInWoonland: this.fb.group({
                begindatum: null as unknown as Date,
                einddatum: null as unknown as Date
            })
        });
    }

    ngOnInit(): void {
        this.dataService.incomeTaxEntity.pipe(takeUntil(this.destroy$)).subscribe((incomeTaxEntity) => {
            this.incomeTaxEntity = incomeTaxEntity;
            if (incomeTaxEntity.aangifte) {
                this.aangeverFg.patchValue(incomeTaxEntity.aangifte.aangever, { emitEvent: false });
                if (incomeTaxEntity.aangifte.gezamenlijkeAangifte !== undefined) {
                    this.gezamenlijkeAangifteFg.patchValue({ gezamenlijkeAangifte: incomeTaxEntity.aangifte.gezamenlijkeAangifte }, { emitEvent: false });
                }
                if (incomeTaxEntity.aangifte.aangever.burgerlijkeStaat === AangeverBurgerlijkeStaat.ALLEENSTAAND) {
                    this.dataService.optimaleAangifte.unsubscribe();
                }
                if (incomeTaxEntity.aangifte.buitenlandseBelastingplicht?.woonlandGegevens?.at(0)) {
                    this.patchWoonlandGegevens(String(incomeTaxEntity.year));
                }
                if (incomeTaxEntity.aangifte.partnerAangifte?.buitenlandseBelastingplicht?.woonlandGegevens?.at(0)) {
                    this.patchWoonlandGegevens(String(incomeTaxEntity.year));
                }
                this.isKwalificerendBtlSubscribtion = this.dataService.isKwalificerendBtl.subscribe((gezien) => {
                    this.isKwalificerendBtl = this.incomeTaxEntity.aangifte?.buitenlandseBelastingplicht?.belastingplichtigeVoldoetAanVoorwaardenKwalificatie.valueOf();
                });
            }
        });
        this.dataAlgService.client.pipe(takeUntil(this.destroy$)).subscribe((client) => {
            this.clientId = client.sstRelationId!;
        });
        this.cdr.detectChanges();
    }

    ngOnDestroy(): void {
        if (this.incomeTaxEntity?.sstRelationId) {
            this.dataService.postIncomeTaxEntity(
                this.incomeTaxEntity.sstRelationId,
                false,
                this.incomeTaxEntity
            );
        }
        if (this.isKwalificerendBtlSubscribtion) {
            this.isKwalificerendBtlSubscribtion.unsubscribe();
        }
        this.destroy$.next();
        this.destroy$.complete();
    }

    public patchWoonlandGegevens(year: string, save = false) {
        const buitenlandseBelastingplicht = this.incomeTaxEntity.aangifte?.buitenlandseBelastingplicht;
        const woonland = buitenlandseBelastingplicht?.woonlandGegevens[0] as Woonland;

        const woonland1 = {
            landCodeWoonland: woonland.landCodeWoonland ? woonland.landCodeWoonland : null,
            periodeWoonachtigInWoonland: {
                begindatum: new Date(`${year}-01-01`),
                einddatum: new Date(`${year}-12-31`)
            } as Periode
        } as Woonland;

        this.woonlandFg.patchValue(woonland1);

        const flags = this.berekenWoonlandFlags(woonland1.landCodeWoonland);
        this.woonlandFg.patchValue(flags);

        if (buitenlandseBelastingplicht?.belastingplichtigeVoldoetAanVoorwaardenKwalificatie !== undefined) {
            this.woonlandFg.patchValue({
                belastingplichtigeVoldoetAanVoorwaardenKwalificatie: buitenlandseBelastingplicht.belastingplichtigeVoldoetAanVoorwaardenKwalificatie
            });
        }

        if (save) {
            this.saveForm();
        }
    }

    private berekenWoonlandFlags(landCode: string): { inwonerLandNonDiscriminatie: boolean; woonlandVoldoetVoorKwalificatie: boolean } {
        return {
            inwonerLandNonDiscriminatie: ['BEL', 'SUR', 'AWB'].includes(landCode),
            woonlandVoldoetVoorKwalificatie: ['ALA', 'ABW', 'BEL', 'BES', 'BGR', 'CYP', 'PRT', 'DNK', 'DEU', 'EST', 'FIN',
                'FRA', 'GUF', 'GRC', 'GLP', 'HUN', 'IRL', 'ITA', 'HRV', 'LVA', 'LTU', 'LIE',
                'LUX', 'MLT', 'MTQ', 'MYT', 'NOR', 'AUT', 'POL', 'PRT', 'REU', 'ROU', 'MAF',
                'SVN', 'SVK', 'ESP', 'SUR', 'CZE', 'SWE', 'CHE'].includes(landCode)
        };
    }

    public saveForm() {
        setTimeout(() => {
            const landCode = this.woonlandFg.getRawValue().landCodeWoonland;
            const flags = this.berekenWoonlandFlags(landCode);
            this.woonlandFg.patchValue(flags, { emitEvent: false });

            this.aangeverFg.updateValueAndValidity();
            this.gezamenlijkeAangifteFg.updateValueAndValidity();
            this.woonlandFg.updateValueAndValidity();
            this.incomeTaxEntity.aangifte = {
                ...this.incomeTaxEntity.aangifte,
                aangever: {
                    roepnaam: this.aangeverFg.value.roepnaam || undefined,
                    voorletters: this.aangeverFg.value.voorletters,
                    tussenvoegsels: this.aangeverFg.value.tussenvoegsels || undefined,
                    achternaam: this.aangeverFg.value.achternaam,
                    geboortedatum: this.aangeverFg.value.geboortedatum,
                    overlijdensdatum: this.aangeverFg.value.overlijdensdatum,
                    bsn: this.aangeverFg.getRawValue().bsn,
                    burgerlijkeStaat: this.aangeverFg.value.burgerlijkeStaat,
                    samenwoonSituatie: this.aangeverFg.value.samenwoonSituatie || undefined,
                    landCodeNationaliteit: this.aangeverFg.value.landCodeNationaliteit
                },
                buitenlandseBelastingplicht: {
                    ...this.incomeTaxEntity.aangifte?.buitenlandseBelastingplicht,
                    inwonerLandNonDiscriminatie: this.woonlandFg.getRawValue().inwonerLandNonDiscriminatie,
                    woonlandVoldoetVoorKwalificatie: this.woonlandFg.getRawValue().woonlandVoldoetVoorKwalificatie,
                    belastingplichtigeVoldoetAanVoorwaardenKwalificatie: this.woonlandFg.getRawValue().belastingplichtigeVoldoetAanVoorwaardenKwalificatie,
                    woonlandGegevens: [this.woonlandFg.getRawValue()]
                }
            } as Aangifte;
            this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
        }, 0);
    }
}
